import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTable } from '@/components/shared/DataTable';

import { LabelWithHelp } from './HelpTip';
import { resolveUiLocale } from '@/utils/uiLocale';
import type { ForecastProductSummary } from '../types/forecast.types';

function warehouseSummary(
  warehouses: ForecastProductSummary['warehouses'],
  maxItems = 3,
): string {
  if (!warehouses.length) return '—';
  const shown = warehouses.slice(0, maxItems);
  const rest = warehouses.length - shown.length;
  const parts = shown.map((w) => `${w.code} ${w.share_pct}%`);
  if (rest > 0) parts.push(`+${rest}`);
  return parts.join(' · ');
}

function formatQty(value: number, locale: string): string {
  return value.toLocaleString(locale, { maximumFractionDigits: 0 });
}

function MiniTrend({ points, locale }: { points: ForecastProductSummary['trend']; locale: string }) {
  if (!points.length) return <span className="text-gray-400">—</span>;
  const values = points.map((p) => p.qty);
  const max = Math.max(...values, 1);
  return (
    <div className="flex items-end gap-px h-6" title={values.map((v) => formatQty(v, locale)).join(', ')}>
      {values.map((v, i) => (
        <div
          key={i}
          className="w-1 rounded-sm bg-sky-400"
          style={{ height: `${Math.max(4, Math.round((v / max) * 24))}px` }}
        />
      ))}
    </div>
  );
}

interface Props {
  rows: ForecastProductSummary[];
  loading?: boolean;
  accuracyMetricLabel: string;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export function ForecastProductTable({
  rows,
  loading,
  accuracyMetricLabel,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onPageSizeChange,
}: Props) {
  const { t, i18n } = useTranslation();
  const locale = resolveUiLocale(i18n.language);

  const columns = useMemo(
    () => [
      {
        header: (
          <LabelWithHelp
            label={t('forecastPage.productColumn')}
            help={t('forecastPage.help.columnProduct')}
          />
        ),
        accessor: 'sku',
        cell: (_: string, row: ForecastProductSummary) => (
          <div className="min-w-0">
            <div className="font-mono text-xs font-medium text-gray-900">{row.sku}</div>
            <div className="truncate text-[10px] text-gray-500" title={row.name}>
              {row.name}
            </div>
          </div>
        ),
      },
      {
        header: (
          <LabelWithHelp
            label={t('forecastPage.abcColumn')}
            help={t('forecastPage.help.columnAbc')}
          />
        ),
        accessor: 'abc_class',
        width: '56px',
        align: 'center' as const,
        cell: (value: string) => (
          <span className="inline-flex rounded px-1.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-700">
            {value && value !== 'unclassified' ? value : '—'}
          </span>
        ),
      },
      {
        header: (
          <LabelWithHelp
            label={t('forecastPage.totalForecastColumn')}
            help={t('forecastPage.help.columnTotal')}
          />
        ),
        accessor: 'total_forecast_qty',
        align: 'right' as const,
        numeric: true,
        cell: (value: number) => formatQty(value, locale),
      },
      {
        header: (
          <LabelWithHelp
            label={t('forecastPage.trendColumn')}
            help={t('forecastPage.help.columnTrend')}
          />
        ),
        accessor: 'trend',
        cell: (_: unknown, row: ForecastProductSummary) => (
          <MiniTrend points={row.trend} locale={locale} />
        ),
      },
      {
        header: (
          <LabelWithHelp
            label={t('forecastPage.warehousesColumn')}
            help={t('forecastPage.help.columnWarehouses')}
          />
        ),
        accessor: 'warehouses',
        cell: (_: unknown, row: ForecastProductSummary) => (
          <span className="text-xs text-gray-600">{warehouseSummary(row.warehouses)}</span>
        ),
      },
      {
        header: (
          <LabelWithHelp
            label={t('forecastPage.sourceColumn')}
            help={t('forecastPage.help.columnSource')}
          />
        ),
        accessor: 'forecaster',
        cell: (value: string | null) => (
          <span className="text-xs text-gray-600">{value || '—'}</span>
        ),
      },
      {
        header: (
          <LabelWithHelp
            label={t('forecastPage.accuracyColumn')}
            help={t('forecastPage.help.columnAccuracy')}
          />
        ),
        accessor: 'accuracy',
        align: 'right' as const,
        cell: (value: number | null) => (
          <span className="text-xs text-gray-500" title={accuracyMetricLabel}>
            {value != null ? `${(value * 100).toFixed(1)}%` : '—'}
          </span>
        ),
      },
    ],
    [t, locale, accuracyMetricLabel],
  );

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <DataTable
      columns={columns}
      data={rows}
      pagination={false}
      loading={loading}
      compact
      externalPagination={{
        currentPage: page,
        totalPages,
        totalCount,
        pageSize,
        onPageChange,
        onPageSizeChange,
      }}
    />
  );
}
